import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Modal, Space, Typography } from 'antd';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import V4Tabs from './V4Tabs';

const DocumentsImportJobsV4 = () => {
  const { getAuthHeaders } = useAuth();
  const headersAuth = useMemo(() => ({ ...getAuthHeaders() }), [getAuthHeaders]);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState('');
  const [perfSummary, setPerfSummary] = useState({ jobs: [], overall_percentiles: {}, jobs_count: 0, account_events: 0 });
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfError, setPerfError] = useState('');
  const [copiedBatchEnv, setCopiedBatchEnv] = useState('');
  const [logModal, setLogModal] = useState({ open: false, job: null, log: '' });

  const loadJobs = useCallback(async () => {
    try {
      setJobsLoading(true);
      setJobsError('');
      const resp = await fetch(`${API_BASE_URL}/api/v4/documents/import/jobs`, { headers: headersAuth });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Failed to load import jobs');
      }
      setJobs(data.jobs || []);
    } catch (err) {
      setJobsError(err?.message || 'Failed to load import jobs');
    } finally {
      setJobsLoading(false);
    }
  }, [headersAuth]);

  const fetchJobDetail = useCallback(async (jobId) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v4/documents/import/jobs/${jobId}`, { headers: headersAuth });
      const data = await resp.json();
      if (resp.ok && data.job) {
        setLogModal({ open: true, job: data.job, log: data.job.error_log || '' });
      } else {
        const detail = data?.detail || 'Unable to load job log';
        alert(detail);
      }
    } catch (err) {
      alert(`Unable to load job log: ${err}`);
    }
  }, [headersAuth]);

  const loadPerfSummary = useCallback(async () => {
    try {
      setPerfLoading(true);
      setPerfError('');
      const params = new URLSearchParams({
        limit_jobs: '10',
        max_lines: '80000',
      });
      const resp = await fetch(`${API_BASE_URL}/api/v4/documents/import/perf?${params.toString()}`, { headers: headersAuth });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Failed to load import performance summary');
      }
      setPerfSummary({
        jobs: data.jobs || [],
        overall_percentiles: data.overall_percentiles || {},
        jobs_count: data.jobs_count || 0,
        account_events: data.account_events || 0,
      });
    } catch (err) {
      setPerfError(err?.message || 'Failed to load import performance summary');
    } finally {
      setPerfLoading(false);
    }
  }, [headersAuth]);

  const perfRecommendations = useMemo(() => {
    const recs = [];
    const top = (perfSummary.jobs || [])[0];
    if (!top) return recs;
    const metrics = top.metrics || {};
    const totalP95 = metrics?.total_ms?.p95 || 0;
    const prefetchP95 = metrics?.prefetch_ms?.p95 || 0;
    const bulkP95 = metrics?.bulk_write_ms?.p95 || 0;
    const batchSize = top.batch_size || 0;

    if (totalP95 > 0) {
      recs.push(`Current slowest job p95 total is ${(totalP95 / 1000).toFixed(2)}s (job ${top.job_id.slice(0, 8)}...).`);
    }
    if (bulkP95 > prefetchP95 * 1.5 && batchSize > 0 && batchSize < 300) {
      recs.push(`Bulk write dominates prefetch. Try increasing V4_IMPORT_DB_BATCH_SIZE from ${batchSize} to ${Math.min(400, batchSize + 50)}.`);
    } else if (prefetchP95 > bulkP95 * 1.5 && batchSize > 80) {
      recs.push(`Prefetch dominates bulk write. Try reducing V4_IMPORT_DB_BATCH_SIZE from ${batchSize} to ${Math.max(50, batchSize - 50)}.`);
    } else if (batchSize > 0) {
      recs.push(`Batch size ${batchSize} looks balanced for current profile. Keep it and monitor p95 over more jobs.`);
    }
    return recs;
  }, [perfSummary.jobs]);

  const suggestedBatchEnvLine = useMemo(() => {
    const top = (perfSummary.jobs || [])[0];
    if (!top) return '';
    const metrics = top.metrics || {};
    const prefetchP95 = metrics?.prefetch_ms?.p95 || 0;
    const bulkP95 = metrics?.bulk_write_ms?.p95 || 0;
    const batchSize = top.batch_size || 0;
    if (!batchSize) return '';

    let suggested = batchSize;
    if (bulkP95 > prefetchP95 * 1.5 && batchSize < 300) {
      suggested = Math.min(400, batchSize + 50);
    } else if (prefetchP95 > bulkP95 * 1.5 && batchSize > 80) {
      suggested = Math.max(50, batchSize - 50);
    }
    return `V4_IMPORT_DB_BATCH_SIZE=${suggested}`;
  }, [perfSummary.jobs]);

  const copySuggestedBatchEnv = useCallback(async () => {
    if (!suggestedBatchEnvLine) return;
    try {
      await navigator.clipboard.writeText(suggestedBatchEnvLine);
      setCopiedBatchEnv(suggestedBatchEnvLine);
      setTimeout(() => setCopiedBatchEnv(''), 1800);
    } catch (err) {
      alert(`Copy failed. Please copy manually: ${suggestedBatchEnvLine}`);
    }
  }, [suggestedBatchEnvLine]);

  useEffect(() => {
    loadJobs();
    loadPerfSummary();
    const id = setInterval(() => {
      loadJobs();
      loadPerfSummary();
    }, 5000);
    return () => clearInterval(id);
  }, [loadJobs, loadPerfSummary]);

  return (
    <div style={{ width: '100%', padding: '16px 20px' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>Documents v4 Import Jobs</Typography.Title>
          <Typography.Text type="secondary">
            Monitor import progress, status, counts, and error logs for V4 document imports.
          </Typography.Text>
        </div>
        <V4Tabs />
        <Card bordered={false} style={{ borderRadius: 12 }}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
            <h6 className="mb-0">Import Performance Insights</h6>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadPerfSummary} disabled={perfLoading}>
              {perfLoading ? 'Refreshing…' : 'Refresh Perf'}
            </button>
          </div>
          {perfError && (
            <Alert type="warning" showIcon message={perfError} className="mb-3" />
          )}
          <div className="row g-3 mb-3">
            {['parse_ms', 'prefetch_ms', 'bulk_write_ms', 'total_ms'].map((metricKey) => {
              const metric = (perfSummary.overall_percentiles || {})[metricKey] || {};
              const labelMap = {
                parse_ms: 'Parse',
                prefetch_ms: 'Prefetch',
                bulk_write_ms: 'Bulk Write',
                total_ms: 'Total',
              };
              return (
                <div className="col-12 col-sm-6 col-xl-3" key={metricKey}>
                  <div className="border rounded p-3 h-100">
                    <div className="small text-uppercase text-muted">{labelMap[metricKey]}</div>
                    <div className="fw-semibold mt-1">p50: {metric.p50 != null ? `${metric.p50} ms` : '—'}</div>
                    <div className="small text-muted">p95: {metric.p95 != null ? `${metric.p95} ms` : '—'}</div>
                    <div className="small text-muted">Samples: {metric.count || 0}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="small text-muted mb-2">
            Jobs profiled: {perfSummary.jobs_count || 0} • Events considered: {perfSummary.account_events || 0}
          </div>
          <div className="table-responsive mb-3">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Mode</th>
                  <th>Rows</th>
                  <th>Batch</th>
                  <th>Parse (p50/p95)</th>
                  <th>Prefetch (p50/p95)</th>
                  <th>Bulk (p50/p95)</th>
                  <th>Total (p50/p95)</th>
                </tr>
              </thead>
              <tbody>
                {(perfSummary.jobs || []).map((job) => (
                  <tr key={job.job_id}>
                    <td><code>{job.job_id}</code></td>
                    <td>{job.mode || '—'}</td>
                    <td>{job.rows || 0}</td>
                    <td>{job.batch_size || '—'}</td>
                    <td>{job.metrics?.parse_ms?.p50 ?? '—'} / {job.metrics?.parse_ms?.p95 ?? '—'}</td>
                    <td>{job.metrics?.prefetch_ms?.p50 ?? '—'} / {job.metrics?.prefetch_ms?.p95 ?? '—'}</td>
                    <td>{job.metrics?.bulk_write_ms?.p50 ?? '—'} / {job.metrics?.bulk_write_ms?.p95 ?? '—'}</td>
                    <td className="fw-semibold">{job.metrics?.total_ms?.p50 ?? '—'} / {job.metrics?.total_ms?.p95 ?? '—'}</td>
                  </tr>
                ))}
                {(!perfSummary.jobs || perfSummary.jobs.length === 0) && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">No import performance data yet. Run an import and refresh.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {perfRecommendations.length > 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-3"
              message={(
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
                  <span className="fw-semibold">Batch Size Hints</span>
                  {suggestedBatchEnvLine && (
                    <span className="d-flex align-items-center gap-2">
                      <code className="small">{suggestedBatchEnvLine}</code>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={copySuggestedBatchEnv}>
                        {copiedBatchEnv ? 'Copied' : 'Copy Env'}
                      </button>
                    </span>
                  )}
                </div>
              )}
              description={(
                <ul className="mb-0 ps-3">
                  {perfRecommendations.map((msg, idx) => (
                    <li key={idx} className="small">{msg}</li>
                  ))}
                </ul>
              )}
            />
          )}

          <hr className="my-4" />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Recent Import Jobs</h6>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadJobs} disabled={jobsLoading}>
              {jobsLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {jobsError && <Alert type="error" showIcon message={jobsError} className="mb-3" />}
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>File</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Products (ins / upd)</th>
                  <th>Docs / Links</th>
                  <th>Errors</th>
                  <th>Started</th>
                  <th>Finished</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const total = job.total_rows || 0;
                  const processed = job.processed_rows || 0;
                  const pct = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                  const jobMode =
                    job.import_mode === 'document_link'
                      ? 'Document Link'
                      : job.import_mode === 'cpn'
                      ? 'CPN'
                      : job.import_mode === 'alt_part_number'
                      ? 'Alt Part Number'
                      : job.import_mode === 'supplier'
                      ? 'Supplier'
                      : 'Product';
                  const productStats =
                    job.import_mode === 'document_link'
                      ? '—'
                      : job.import_mode === 'alt_part_number' || job.import_mode === 'supplier'
                      ? `${job.inserted_products || 0} new`
                      : `${job.inserted_products || 0} / ${job.updated_products || 0}`;
                  const docsOrLinks =
                    job.import_mode === 'document_link' || job.import_mode === 'alt_part_number' || job.import_mode === 'supplier'
                      ? (job.document_links_upserted || 0)
                      : (job.documents_updated || 0);
                  return (
                    <tr key={job.job_id}>
                      <td><code>{job.job_id}</code></td>
                      <td>{job.filename || '—'}</td>
                      <td>{jobMode}</td>
                      <td>
                        <span className={`badge ${
                          job.status === 'completed' ? 'bg-success' :
                          job.status === 'failed' ? 'bg-danger' :
                          job.status === 'running' ? 'bg-warning text-dark' :
                          'bg-secondary'
                        }`}>
                          {job.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 160 }}>
                        {total > 0 ? (
                          <>
                            <div className="small text-muted mb-1">{processed} / {total}</div>
                            <div className="progress" style={{ height: 6 }}>
                              <div className="progress-bar" style={{ width: `${pct}%` }} />
                            </div>
                          </>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td>{productStats}</td>
                      <td>{docsOrLinks}</td>
                      <td>{job.errors_count || 0}</td>
                      <td>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</td>
                      <td>{job.finished_at ? new Date(job.finished_at).toLocaleString() : '—'}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => fetchJobDetail(job.job_id)}
                        >
                          {job.errors_count ? `View Errors (${job.errors_count})` : 'View Log'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {jobs.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center text-muted">No import jobs yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </Space>

      <Modal
        title="Import Job Log"
        open={logModal.open}
        onCancel={() => setLogModal({ open: false, job: null, log: '' })}
        footer={null}
        width={880}
      >
        <div className="small text-muted mb-2">{logModal.job?.job_id}</div>
        {logModal.log ? (
          <pre className="bg-light p-3 rounded" style={{ maxHeight: 360, overflow: 'auto' }}>{logModal.log}</pre>
        ) : (
          <div className="text-muted">No errors were recorded for this job.</div>
        )}
      </Modal>
    </div>
  );
};

export default DocumentsImportJobsV4;
